import {
  compareVersion,
  parseVersion,
  Version,
  VersionWithModifier,
} from '../../utils/version';

/**
 * Check if the given version matches against a target version or version range.
 * @param versionStr in this context should be the app's binary version
 * @param targetVersion the target version (or version range) set as metadata
 *   of the ota updates when publish ota updates, e.g., "3.60", "3.60-3.60.2"
 * @returns true if match otherwise false
 */
export function isVersionMatchingTarget(
  versionStr: string,
  targetVersion: string,
) {
  const version = parseVersion(versionStr, '=')?.version;

  // this should not be the case, just for sure
  if (version == null) {
    return false;
  }

  // if target version is empty, it means no version targeting is posed
  if (!targetVersion) {
    return true;
  }

  const res = targetVersion.split(/[-,]/);
  const [lowerStr, upperStr] = [res[0].trim(), res[1]?.trim()];

  const lower = parseVersion(lowerStr, '>=');
  if (lower == null) {
    return false;
  }

  const upper = upperStr ? parseVersion(upperStr, '<=') : undefined;
  if (upperStr && upper == null) {
    return false;
  }

  return (
    matchVersionWithTarget(version, lower) &&
    (!upper || matchVersionWithTarget(version, upper))
  );
}

function matchVersionWithTarget(version: Version, target: VersionWithModifier) {
  // we ensure the versions must stay at the same release channel
  if (
    version.major !== target.version.major ||
    version.minor !== target.version.minor
  ) {
    return false;
  }

  if (target.modifier === '~') {
    return true;
  }

  const order = compareVersion(version, target.version);
  if (target.modifier === '=' || target.modifier === '==') {
    return order === 0;
  }
  if (target.modifier === '>=' || target.modifier === '~=') {
    return order >= 0;
  }
  if (target.modifier === '<=') {
    return order <= 0;
  }
  if (target.modifier === '>') {
    return order > 0;
  }
  if (target.modifier === '<') {
    return order < 0;
  }
  return false;
}
